import { ensureFreshTokens } from '../../helpers/auth'

export type QueryParamValue = string | number | boolean | null | undefined

export type RequestHandlerPropTypes = {
  method?: RequestInit['method']
  url: string | URL
  headers?: RequestInit['headers']
  params?: Record<string, QueryParamValue>
  body?: unknown
  timeout?: number
  authenticate?: boolean
  acceptedStatuses?: number[]
  signal?: AbortSignal
}

const DEFAULT_TIMEOUT = 30000
const AUTH_RETRY_STATUSES = new Set([401, 403])

const isBodyInit = (value: unknown): value is BodyInit => {
  return (
    typeof value === 'string' ||
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value)
  )
}

const toUrl = (url: string | URL) => {
  if (url instanceof URL) {
    return new URL(url.toString())
  }

  return new URL(url, window.location.origin)
}

export const buildApiUrl = (path: string) => {
  const normalizedPath = path.replace(/^\//, '')
  const apiBaseUrl = import.meta.env.VITE_API_URL.endsWith('/')
    ? import.meta.env.VITE_API_URL
    : `${import.meta.env.VITE_API_URL}/`

  return new URL(normalizedPath, apiBaseUrl)
}

type RequestHandlerWithoutUrl = Omit<RequestHandlerPropTypes, 'url'>

const hasErrorMessage = (value: unknown): value is { error: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error?: unknown }).error === 'string' &&
    (value as { error: string }).error.length > 0
  )
}

const appendQueryParams = (url: URL, params?: Record<string, QueryParamValue>) => {
  if (!params) {
    return
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    url.searchParams.append(key, String(value))
  })
}

const buildRequestBody = (body: unknown, headers: Headers, method: RequestInit['method']) => {
  if (body === undefined || body === null || method === 'GET' || method === 'HEAD') {
    return undefined
  }

  if (isBodyInit(body)) {
    return body
  }

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return JSON.stringify(body)
}

const fetchWithTimeout = async (
  input: string,
  init: RequestInit,
  timeout: number,
  signal?: AbortSignal
): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeout)

  const abortListener = () => controller.abort()
  signal?.addEventListener('abort', abortListener)

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    })
  } catch (error) {
    if (controller.signal.aborted && !signal?.aborted) {
      throw new Error(`Timeout: ${input}`)
    }

    throw error
  } finally {
    window.clearTimeout(timeoutId)
    signal?.removeEventListener('abort', abortListener)
  }
}

const buildRequestInit = (props: RequestHandlerPropTypes, accessToken?: string | null): RequestInit => {
  const method = props.method ?? 'GET'
  const headers = new Headers(props.headers)

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return {
    method,
    headers,
    body: buildRequestBody(props.body, headers, method),
  }
}

const shouldRetryForAuth = (response: Response, authenticate: boolean) => {
  return authenticate && AUTH_RETRY_STATUSES.has(response.status)
}

export const RequestHandler = async (props: RequestHandlerPropTypes): Promise<Response> => {
  const { url, params, timeout = DEFAULT_TIMEOUT, authenticate = true, acceptedStatuses = [], signal } = props
  const requestUrl = toUrl(url)
  appendQueryParams(requestUrl, params)

  const initialTokens = authenticate ? await ensureFreshTokens() : null
  const initialAccessToken = initialTokens?.accessToken ?? null
  let response = await fetchWithTimeout(
    requestUrl.toString(),
    buildRequestInit(props, initialAccessToken),
    timeout,
    signal
  )

  if (acceptedStatuses.includes(response.status)) {
    return response
  }

  if (!shouldRetryForAuth(response, authenticate)) {
    return response
  }

  const refreshedTokens = await ensureFreshTokens({ forceRefresh: true })

  if (!refreshedTokens?.accessToken || refreshedTokens.accessToken === initialAccessToken) {
    return response
  }

  if (response.body) {
    await response.body.cancel()
  }

  response = await fetchWithTimeout(
    requestUrl.toString(),
    buildRequestInit(props, refreshedTokens.accessToken),
    timeout,
    signal
  )

  return response
}

export const requestJson = async <T>(props: RequestHandlerPropTypes): Promise<T> => {
  const response = await RequestHandler(props)
  return (await response.json()) as T
}

export const requestJsonOrThrow = async <T>(props: RequestHandlerPropTypes): Promise<T> => {
  const data = await requestJson<T | { error?: string }>(props)

  if (hasErrorMessage(data)) {
    throw new Error(data.error)
  }

  return data as T
}

export const requestApi = (path: string, props: RequestHandlerWithoutUrl = {}) => {
  return RequestHandler({
    ...props,
    url: buildApiUrl(path),
  })
}

export const requestApiJson = <T>(path: string, props: RequestHandlerWithoutUrl = {}) => {
  return requestJson<T>({
    ...props,
    url: buildApiUrl(path),
  })
}

export const requestApiJsonOrThrow = <T>(path: string, props: RequestHandlerWithoutUrl = {}) => {
  return requestJsonOrThrow<T>({
    ...props,
    url: buildApiUrl(path),
  })
}
